"use client";

// Profile component
import React, { useState, useEffect} from 'react';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import Card from '@/components/Card'
import CardWithUser from '@/components/CardWithUser'
import { UserProduct, ProductLocation } from "@prisma/client";
import type { Stripe } from '@stripe/stripe-js'
import { loadStripe } from "@stripe/stripe-js";
import { CheckoutSubscribeDialog } from './checkout-subscribe-dialog';
import { Dialog, DialogContent } from './ui/dialog';
import { CheckoutCompleteDialog } from './checkout-complete-dialog';
import { useSearchParams } from 'next/navigation';

interface CardContainerProps {
    primaryColour: string;
    primaryBackgroundColour: string;
    secondaryBackgroundColour: string;
    cardStyle: string | null;
    isLive: boolean;
    isStripeConnectOnboarded: boolean;
    stripeConnectAccountId: string | null;
    profileImageUrl: string | null;
    urlSlug: string;
    name: string | null;
    userId: string;
    userProduct: (UserProduct & {
        userReview: {
            name: string | null;
            title: string | null;
            rating: number | null;
        }[];
    })[];
    selectedUserReview: {
        title: string | null;
        name: string | null;
        rating: number | null;
    };
    locationProducts: (ProductLocation & {
        product: UserProduct & {
            user: {
                name: string | null;
                bio: string | null;
                profileImageUrl: string | null;
                urlSlug: string;
                isPremium: boolean;
                stripeConnectAccountId: string | null;
                isStripeConnectOnboarded: boolean;
            } | null;
        };
    })[]
}

const CardContainer: React.FC<CardContainerProps> = ({primaryColour, primaryBackgroundColour, secondaryBackgroundColour, cardStyle, profileImageUrl, urlSlug, name, userId, isLive, userProduct, locationProducts, stripeConnectAccountId, isStripeConnectOnboarded, selectedUserReview }) => {
    async function fetchClientSecret(email: string, firstName: string, lastName: string, title: string, userId: string, stripePriceId: string) {
        // Replace with the actual logic to call your API and get the client secret
        const response = await fetch('/api/connect/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            firstName: firstName,
            lastName: lastName,
            title: title,
            priceId: stripePriceId,
            userId: userId
          }),
        });
        const data = await response.json();
        setClientSecret(data.client_secret);
        setCheckoutKey(prevKey => prevKey + 1);
        return data.client_secret; // Ensure your API returns the client secret
      }

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [checkoutKey, setCheckoutKey] = useState<number>(0);
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
    const [isCheckoutSubscribeDialogOpen, setIsCheckoutSubscribeDialogOpen] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [isCheckoutStripeDialogOpen, setIsCheckoutStripeDialogOpen] = useState(false);
    const [isCheckoutCompleteDialogOpen, setIsCheckoutCompleteDialogOpen] = useState(false);

    // Handler to open the CheckoutSubscribeDialog
    const handleCardClick = (productId: number) => {
        setSelectedProductId(productId);
        setIsCheckoutSubscribeDialogOpen(true);
    };
    // This function will re-run if the accountId prop changes.
    useEffect(() => {
        const fetchStripeObject = async () => {
        // If there is no accountId, do not run the loadStripe function.
        if (stripeConnectAccountId && isStripeConnectOnboarded) {
            const res = await loadStripe(
                process.env.NEXT_PUBLIC_STRIPE_CONNECT_PUBLISHABLE_KEY_LIVE ?? process.env.NEXT_PUBLIC_STRIPE_CONNECT_PUBLISHABLE_KEY ?? '',
            {
                stripeAccount: stripeConnectAccountId
            }
            );
            // When we have got the Stripe object, pass it into our useState.
            setStripePromise(res);
        }
        };
        fetchStripeObject();
    }, [stripeConnectAccountId]);

    const searchParams = useSearchParams();
    const sessionId = searchParams.get('session_id');
    useEffect(() => {
        if (sessionId) {
          setIsCheckoutStripeDialogOpen(false);
          setIsCheckoutCompleteDialogOpen(true);
        }
      }, [sessionId]);
    
    return (
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }} key={checkoutKey}>
          <div id="products" style={{ backgroundColor: secondaryBackgroundColour }} className={`${isLive ? '' : 'blur'}`}>
              <div style={{ backgroundColor: secondaryBackgroundColour }} className="flex flex-col items-center justify-center mx-4 py-4">
              {userProduct.map((product, index) => (
                  <Card key={index}
                        id={product.id}
                        title={product.title}
                        description={product.description}
                        url={product.url}
                        hexColour={primaryColour}
                        primaryBackgroundColour={primaryBackgroundColour}
                        cardStyle={cardStyle}
                        isPaidProduct={product.isPaidProduct}
                        price={product.price}
                        stripePriceId={product.stripePriceId}
                        userId={product.userId}
                        onCardClick={handleCardClick}
                  />
              ))}
              {locationProducts.map((product, index) => (
                  <CardWithUser key={index}
                                title={product.product.title}
                                description={product.product.description}
                                url={product.product.url}
                                hexColour={primaryColour}
                                primaryBackgroundColour={primaryBackgroundColour}
                                cardStyle={cardStyle}
                                userName={product.product.user?.name}
                                profileImageUrl={product.product.user?.profileImageUrl}
                                urlSlug={product.product.user?.urlSlug}
                    />
              ))}
              </div>
          </div>
          {selectedProductId && userProduct.filter(product => product.id === selectedProductId).map((product, index) => (
                        <CheckoutSubscribeDialog
                            key={index}
                            userId={product.userId}
                            title={product.title}
                            isPaidProduct={product.isPaidProduct}
                            price={product.price}
                            productId={product.id}
                            stripePriceId={product.stripePriceId}
                            profileImageUrl={profileImageUrl}
                            slug={urlSlug}
                            name={name}
                            hexColour={primaryColour}
                            selectedReview={product.userReview[0] || selectedUserReview}
                            fetchClientSecret={fetchClientSecret}
                            isOpen={isCheckoutSubscribeDialogOpen}
                            onClose={() => setIsCheckoutSubscribeDialogOpen(false)}
                            onOpenChange={setIsCheckoutSubscribeDialogOpen}
                            onSuccessfulSubscription={() => setIsCheckoutStripeDialogOpen(true)}
                        />
                )
            )}
            {clientSecret && isCheckoutStripeDialogOpen && 
                (
                    <Dialog open={isCheckoutStripeDialogOpen} onOpenChange={setIsCheckoutStripeDialogOpen}>
                        <DialogContent className="flex flex-col sm:max-h-[80%] overflow-y-auto h-full sm:h-auto">
                        <EmbeddedCheckout />
                        </DialogContent>
                    </Dialog>
                )
            }
            { sessionId && (
                <CheckoutCompleteDialog 
                    userId={userId}
                    name={name}
                    slug={urlSlug}
                    hexColour={primaryColour}
                    sessionId={sessionId}
                    isOpen={isCheckoutCompleteDialogOpen}
                    onOpenChange={setIsCheckoutCompleteDialogOpen}
                />
            )}
        </EmbeddedCheckoutProvider>
        // <EmbeddedCheckoutProvider stripe={stripePromise} options={{clientSecret: 'cs_test_a1pA7T3Af2okgqBhZksrnteRRJtIbKRVFHCCTqnJ1CszZiXO3tUkIyVY5Z_secret_fidwbEhqYWAnPydgaGdgYWFgYScpJ2lkfGpwcVF8dWAnPyd2bGtiaWBabHFgaCcpJ3dgYWx3YGZxSmtGamh1aWBxbGprJz8nZGlyZHx2J3gl'}}>
        //     <EmbeddedCheckout />
        // </EmbeddedCheckoutProvider>
    )
}

export default CardContainer;